import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  styled,
  useGalleryControls,
  useMediaGalleryContext,
} from '@volkswagen-onehub/components-core';
import { minimalScreenWidth } from '@/common/breakpoints';
import { normalizeIndex } from './gallery.utils';

type GalleryThumbnailsProps = {
  assets: string[];
  labelPrefix: string;
};

type ThumbnailScrollDirection = 'previous' | 'next';

type ThumbnailScrollState = {
  canScrollPrevious: boolean;
  canScrollNext: boolean;
};

const StyledThumbnailSlider = styled.div`
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) 36px;
  column-gap: 8px;
  align-items: center;
  width: 100%;
  margin-top: ${({ theme }) => theme.size.static100};
`;

const StyledThumbnailScrollButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.content.primary};
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
  font-size: 24px;
  line-height: 1;

  &:disabled {
    opacity: 0.25;
    cursor: default;
    pointer-events: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.focus.main};
    outline-offset: 2px;
  }
`;

const StyledThumbnails = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: ${({ theme }) => theme.size.static100};
  scroll-behavior: smooth;
  scroll-snap-type: x proximity;
  overscroll-behavior-x: contain;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledThumbnailButton = styled.button<{ $active: boolean }>`
  flex: 0 0 128px;
  padding: 0 0 6px;
  border: 0;
  background: transparent;
  cursor: pointer;
  overflow: visible;
  position: relative;
  scroll-snap-align: start;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3px;
    border-radius: 999px;
    background-color: ${({ theme }) => theme.colors.content.primary};
    opacity: ${({ $active }) => ($active ? 1 : 0)};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.focus.main};
    outline-offset: 2px;
  }

  @media screen and ${minimalScreenWidth.m} {
    flex-basis: 152px;
  }
`;

const StyledThumbnailImageWrapper = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.size.borderRadius.mediaGallery};
  background-color: ${({ theme }) => theme.colors.background.primary};
`;

const StyledThumbnailImage = styled.img<{ $active: boolean }>`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: ${({ $active }) => ($active ? 'none' : 'brightness(0.52)')};
  transition: filter 0.2s ease;
  user-select: none;
  -webkit-user-drag: none;
`;

export const GalleryThumbnails = ({
  assets,
  labelPrefix,
}: GalleryThumbnailsProps): ReactElement | null => {
  const { t } = useTranslation();
  const { changeSlide } = useGalleryControls();

  const {
    state: { activeIndex, lastIndex, slideLength },
  } = useMediaGalleryContext();

  const thumbnailListRef = useRef<HTMLDivElement | null>(null);
  const activeThumbnailRef = useRef<HTMLButtonElement | null>(null);

  const [thumbnailScrollState, setThumbnailScrollState] = useState<ThumbnailScrollState>({
    canScrollPrevious: false,
    canScrollNext: false,
  });

  const activeImageIndex = normalizeIndex(
    Number.isInteger(activeIndex) ? activeIndex : lastIndex,
    slideLength || assets.length,
  );

  const updateThumbnailScrollState = useCallback(() => {
    const thumbnailList = thumbnailListRef.current;

    if (!thumbnailList) {
      return;
    }

    const scrollTolerance = 2;
    const maxScrollLeft = thumbnailList.scrollWidth - thumbnailList.clientWidth;
    const normalizedScrollLeft = Math.max(0, Math.min(maxScrollLeft, thumbnailList.scrollLeft));

    const nextState = {
      canScrollPrevious: normalizedScrollLeft > scrollTolerance,
      canScrollNext: normalizedScrollLeft < maxScrollLeft - scrollTolerance,
    };

    setThumbnailScrollState((previousState) => {
      if (
        previousState.canScrollPrevious === nextState.canScrollPrevious &&
        previousState.canScrollNext === nextState.canScrollNext
      ) {
        return previousState;
      }

      return nextState;
    });
  }, []);

  const scrollThumbnails = useCallback(
    (direction: ThumbnailScrollDirection) => {
      const thumbnailList = thumbnailListRef.current;

      if (!thumbnailList) {
        return;
      }

      const scrollAmount = Math.max(thumbnailList.clientWidth * 0.75, 180);

      thumbnailList.scrollBy({
        left: direction === 'next' ? scrollAmount : -scrollAmount,
        behavior: 'smooth',
      });

      window.setTimeout(updateThumbnailScrollState, 250);
    },
    [updateThumbnailScrollState],
  );

  useEffect(() => {
    updateThumbnailScrollState();
    window.addEventListener('resize', updateThumbnailScrollState);

    return () => {
      window.removeEventListener('resize', updateThumbnailScrollState);
    };
  }, [assets.length, updateThumbnailScrollState]);

  if (assets.length <= 1) {
    return null;
  }

  return (
    <StyledThumbnailSlider data-gallery-drag-ignore="true">
      <StyledThumbnailScrollButton
        type="button"
        aria-label={t('clusterButtons.previousImageAriaLabel', 'Previous thumbnails')}
        disabled={!thumbnailScrollState.canScrollPrevious}
        onClick={() => scrollThumbnails('previous')}
      >
        ‹
      </StyledThumbnailScrollButton>

      <StyledThumbnails
        ref={thumbnailListRef}
        aria-label={`${labelPrefix} thumbnails`}
        onScroll={updateThumbnailScrollState}
      >
        {assets.map((asset, index) => (
          <StyledThumbnailButton
            key={`${asset}-thumbnail-${index}`}
            ref={index === activeImageIndex ? activeThumbnailRef : undefined}
            type="button"
            $active={index === activeImageIndex}
            aria-label={`${labelPrefix} thumbnail ${index + 1}`}
            aria-current={index === activeImageIndex ? 'true' : undefined}
            onClick={() => changeSlide(index)}
          >
            <StyledThumbnailImageWrapper>
              <StyledThumbnailImage
                src={asset}
                alt=""
                aria-hidden="true"
                $active={index === activeImageIndex}
                loading="lazy"
                decoding="async"
              />
            </StyledThumbnailImageWrapper>
          </StyledThumbnailButton>
        ))}
      </StyledThumbnails>

      <StyledThumbnailScrollButton
        type="button"
        aria-label={t('clusterButtons.nextImageAriaLabel', 'Next thumbnails')}
        disabled={!thumbnailScrollState.canScrollNext}
        onClick={() => scrollThumbnails('next')}
      >
        ›
      </StyledThumbnailScrollButton>
    </StyledThumbnailSlider>
  );
};
